import React from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./RefundPolicy.module.css";

const RefundPolicy: React.FC = () => {
  const { lang } = useParams<{ lang: string }>();
  const { t } = useTranslation();
  const currentLang = lang ?? "en";
  const supportPath = `/${currentLang}/support?type=refund`;

  return (
    <div className={styles.container}>
      <h1 className={`${styles.header} ${styles.title}`}>
        {t("refund_policy_page.title", { defaultValue: "Refund Policy" })}
      </h1>
      <p className={styles.text}>
        {t("refund_policy_page.intro", {
          defaultValue:
            "This policy explains refund and cancellation rules for AUTOVID credit packs and subscription products.",
        })}
      </p>
      <p className={styles.text}>
        {t("refund_policy_page.effective_date", {
          defaultValue:
            "Effective date: April 20, 2026 / Last updated: May 6, 2026",
        })}
      </p>

      <h2 className={`${styles.header} ${styles.subTitle}`}>
        {t("refund_policy_page.section1_title", {
          defaultValue: "1. Credit pack (one-time purchase) refunds",
        })}
      </h2>
      <ul className={styles.list}>
        <li className={styles.item}>
          {t("refund_policy_page.section1_items.0", {
            defaultValue: "Refund requests are accepted within 7 days of purchase.",
          })}
        </li>
        <li className={styles.item}>
          {t("refund_policy_page.section1_items.1", {
            defaultValue:
              "AUTOVID deducts CRD only when a generation task is successfully completed. If a task fails or no output is generated, CRD is generally not deducted.",
          })}
        </li>
        <li className={styles.item}>
          {t("refund_policy_page.section1_items.2", {
            defaultValue:
              "Therefore, deducted CRD is treated as having been used for successfully processed and completed generation tasks, and used CRD is generally excluded from refunds.",
          })}
        </li>
        <li className={styles.item}>
          {t("refund_policy_page.section1_items.3", {
            defaultValue:
              "Only 500-CRD and 1000-CRD packs are currently sold, and the 500-CRD pack is the regular-price baseline.",
          })}
        </li>
        <li className={styles.item}>
          {t("refund_policy_page.section1_items.4", {
            defaultValue: "The refundable amount is calculated using the formula below.",
          })}
        </li>
      </ul>

      <pre className={styles.formula}>
        {t("refund_policy_page.formula", {
          defaultValue:
            "Refund amount = Pre-tax purchase amount - (Used CRD x regular unit price of 500-CRD pack)",
        })}
      </pre>
      <p className={styles.text}>
        {t("refund_policy_page.section1_note1", {
          defaultValue:
            "If the calculated refundable amount is USD 1.00 or less (or the equivalent amount in the payment currency), the refund is not available.",
        })}
      </p>
      <p className={styles.text}>
        {t("refund_policy_page.section1_note2", {
          defaultValue:
            "If a duplicate charge, billing error, service outage, or other clear issue is suspected, contact customer support and we will review the request based on available payment and usage records.",
        })}
      </p>
      <p className={styles.text}>
        {t("refund_policy_page.ai_variance", {
          defaultValue:
            "AI-generated outputs may vary depending on the input and generation process, so a successfully completed task may not be refundable solely because the result differs from the user's expectations.",
        })}
      </p>
      <p className={styles.text}>
        {t("refund_policy_page.output_issue_review", {
          defaultValue:
            "If there is an issue using the output or a clear abnormality is suspected, please contact customer support and we will review the request based on available records.",
        })}
      </p>

      <h2 className={`${styles.header} ${styles.subTitle}`}>
        {t("refund_policy_page.section2_title", {
          defaultValue: "2. Subscription refunds and cancellation",
        })}
      </h2>
      <ul className={styles.list}>
        <li className={styles.item}>
          {t("refund_policy_page.section2_items.0", {
            defaultValue:
              "Subscription charges are generally non-refundable for the current billing period once payment is completed and access has been provided.",
          })}
        </li>
        <li className={styles.item}>
          {t("refund_policy_page.section2_items.1", {
            defaultValue:
              "However, for monthly subscriptions, if subscription-issued CRD has not been used at all, a refund request may be submitted and reviewed after payment and usage records are verified.",
          })}
        </li>
        <li className={styles.item}>
          {t("refund_policy_page.section2_items.2", {
            defaultValue:
              "However, duplicate charges, billing errors, inability to access the service, or other clear issues may be reviewed individually after contacting customer support.",
          })}
        </li>
        <li className={styles.item}>
          {t("refund_policy_page.section2_items.3", {
            defaultValue:
              "When a subscription is canceled, no additional charges are made from the next billing cycle, and any remaining paid access can be used until the end of the current period.",
          })}
        </li>
      </ul>

      <h2 className={`${styles.header} ${styles.subTitle}`}>
        {t("refund_policy_page.section3_title", {
          defaultValue: "3. Contact and processing",
        })}
      </h2>
      <p className={styles.text}>
        {t("refund_policy_page.section3_intro", {
          defaultValue: "Please submit refund requests through the customer support page.",
        })}
      </p>
      <p className={styles.text}>
        {t("refund_policy_page.section3_order_details", {
          defaultValue:
            "Select the refund category and include order details (account, purchase date, product name, and order number).",
        })}
      </p>
      <p className={styles.text}>
        {t("refund_policy_page.section3_review_timeline", {
          defaultValue:
            "Refund requests are usually reviewed within 3-5 business days. Approved refunds are processed to the original payment method, and the payment provider or card issuer may take additional time to reflect the refund.",
        })}
      </p>
      <p className={styles.text}>
        <Link to={supportPath}>
          {t("refund_policy_page.section3_link", {
            defaultValue: "Submit a refund inquiry on the customer support page",
          })}
        </Link>
      </p>

      <p className={styles.notice}>
        {t("refund_policy_page.legal_notice", {
          defaultValue:
            "If applicable law provides stronger consumer rights, those legal rights take precedence over this policy.",
        })}
      </p>
    </div>
  );
};

export default RefundPolicy;
